import * as React from 'react';

import {DashboardCard} from 'shells/dashboard/card/dashboard-card';
import {Illustration} from 'shells/illustrations/illustration';
import {MetricRow} from 'shells/metric-row/metric-row';
import {PromoStateOverlay} from 'shells/promo-state-overlay/promo-state-overlay';

import {OpenableModals, useOpenableModal} from '../../../openable-modals';

import {useGetVIQDashboard} from '../graphql/hooks';
import {ViewAllLink} from '../view-all-link';

import {getNullDeltaDirectionAndMood, getLeadsLink, getVisitorsLink} from './helpers';

// Using these consts because metricRow needs very specific inputs that don't match our queried return values
//  and I don't want to have a bunch of nested ternary operators or something in the component return
const DIRECTION = {
    INCREASE: 'up',
    DECREASE: 'down',
    CONSTANT: 'neutral',
} as const;

const MOOD = {
    INCREASE: 'positive',
    DECREASE: 'negative',
    CONSTANT: 'neutral',
} as const;

const TIMESPAN = {
    '-d7': 'wtd',
    '-d30': 'mtd',
    '-d90': 'qtd',
} as const;

type Props = {
    timespan: string;
};

export function VisitorIqCard(props: Props) {
    const {isLoading, viqDashboard} = useGetVIQDashboard({timespan: props.timespan});
    const totalCount = viqDashboard ? viqDashboard.totalCount : undefined;
    const stats = viqDashboard ? viqDashboard.stats : [];
    const originId = viqDashboard ? viqDashboard.originId : '';
    const {onOpen: onOpenAnalyticsInfoModal} = useOpenableModal(
        OpenableModals.ANALYTICS_ONBOARDING_MODAL
    );
    const {onOpen: onOpenAnalyticsEmbedModal} = useOpenableModal(
        OpenableModals.EMBED_ANALYTICS_MODAL
    );

    return (
        <DashboardCard
            title='VisitorIQ'
            subtitle='Identify prospects visiting your website'
            link={<ViewAllLink totalCount={totalCount} type='VISITOR_IQ' />}
            isLoading={isLoading}
            mainContent={{
                content: (
                    <div className='flex flex-dir-col justify-even full-width'>
                        {stats.map((metric) => {
                            const htmlUrl =
                                metric.name === 'Companies identified'
                                    ? getVisitorsLink(props.timespan)
                                    : getLeadsLink(props.timespan, originId);

                            // metric.formattedPreviousValue can be null, doing this here so I don't have to check null at every usage
                            const formattedPreviousValue = metric.formattedPreviousValue
                                ? metric.formattedPreviousValue
                                : '0';

                            const delta = metric.delta
                                ? metric.delta.direction
                                : getNullDeltaDirectionAndMood(
                                      formattedPreviousValue,
                                      metric.formattedValue
                                  );

                            return (
                                <div key={metric.name}>
                                    <MetricRow
                                        name={metric.name}
                                        // Using the amount object (type FormattedValue) because if we use count, we need to strip the commas else the percent is NaN
                                        // The value still needs commas removed though, else the percent is NaN from casting comma to a number
                                        // TODO later: clean things up so we don't have to do this with the empty strings and removing commas and such
                                        previousValue={{
                                            count: Number(formattedPreviousValue.replace(/,/g, '')),
                                            amount: {
                                                formatted: formattedPreviousValue,
                                                value: Number(
                                                    formattedPreviousValue.replace(/,/g, '')
                                                ),
                                                prefix: '',
                                                suffix: '',
                                            },
                                        }}
                                        currentValue={{
                                            count: Number(metric.formattedValue.replace(/,/g, '')),
                                            amount: {
                                                formatted: metric.formattedValue,
                                                value: Number(
                                                    metric.formattedValue.replace(/,/g, '')
                                                ),
                                                prefix: '',
                                                suffix: '',
                                            },
                                        }}
                                        deltaDirection={DIRECTION[delta]}
                                        deltaMood={MOOD[delta]}
                                        htmlUrl={htmlUrl}
                                        // Can't use suffix string from query delta because if there is no change, delta is null
                                        // but we still need to display this string even when there is no change
                                        // so we get it from the props and map it to the required format. I know its dirty but such is life
                                        // @ts-expect-error
                                        periodString={TIMESPAN[props.timespan]}
                                        timespanString={metric.timespanString}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ),
            }}
            promoStateOverlay={
                viqDashboard && Number(viqDashboard.totalCount) === 0 ? (
                    <PromoStateOverlay
                        promoStateProps={{
                            isHorizontal: true,
                            imageComponent: (
                                <Illustration src='trial-onboarding/marketing-visitor-iq' />
                            ),
                            title: 'VisitorIQ',
                            subtitle:
                                'Identify the specific companies that are browsing your website.',
                            primaryCta: {
                                text: 'Learn more',
                                onClick: () => {
                                    onOpenAnalyticsInfoModal();
                                },
                            },
                            secondaryCta: {
                                text: 'Set up tracking',
                                onClick: () => {
                                    onOpenAnalyticsEmbedModal();
                                },
                            },
                            alignBottom: true,
                            blueImageBackground: true,
                        }}
                    />
                ) : null
            }
        />
    );
}
